import {
  QueryClient,
  type Query,
  type QueryClient as Client,
  type QueryKey,
} from '@tanstack/react-query';
import { ApiError } from './api.js';

/**
 * One `QueryClient` policy for every portal (plan 45 §2, "How we hold them").
 * Lists are fresh for 30 s and settings for 5 min; anything unused leaves the
 * cache after 10 min. A 4xx is an answer, not an outage, so it is never
 * retried; a 5xx or a network failure is retried twice with backoff. Only the
 * screens people keep open (Inbox, Tasks, Dashboard) refetch when the window
 * regains focus. Realtime pushes invalidate exact keys on top of this.
 * The policy only changes when the portal asks again: API calls are the same.
 */
export const QUERY_TIMES = {
  listStaleMs: 30_000,
  settingsStaleMs: 5 * 60_000,
  gcMs: 10 * 60_000,
  maxRetries: 2,
} as const;

export interface QueryPolicy {
  /** First key segments that refetch on window focus (Inbox, Tasks, Dashboard). */
  focusKeys: readonly string[];
  /** First key segments that hold settings: fresh for 5 min. */
  settingsKeys: readonly string[];
}

function head(key: QueryKey): string {
  return typeof key[0] === 'string' ? key[0] : '';
}

/** 4xx never retries; 5xx and network failures retry twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= QUERY_TIMES.maxRetries) return false;
  if (error instanceof ApiError) return error.status >= 500;
  // Anything else is a network failure (fetch threw) or a timeout.
  return true;
}

/** 1 s then 2 s (capped at 8 s), with up to 250 ms of jitter so tabs do not retry in step. */
export function retryDelay(attempt: number, random: () => number = Math.random): number {
  return Math.min(1000 * 2 ** attempt, 8000) + Math.floor(random() * 250);
}

export function staleTimeFor(policy: QueryPolicy, key: QueryKey): number {
  return policy.settingsKeys.includes(head(key))
    ? QUERY_TIMES.settingsStaleMs
    : QUERY_TIMES.listStaleMs;
}

export function refetchOnFocusFor(policy: QueryPolicy, key: QueryKey): boolean {
  return policy.focusKeys.includes(head(key));
}

export function createQueryClient(policy: QueryPolicy): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: (query: Query) => staleTimeFor(policy, query.queryKey),
        gcTime: QUERY_TIMES.gcMs,
        retry: shouldRetry,
        retryDelay: (attempt) => retryDelay(attempt),
        refetchOnWindowFocus: (query: Query) => refetchOnFocusFor(policy, query.queryKey),
      },
      // A write is never repeated on its own: the person decides.
      mutations: { retry: false },
    },
  });
}

/**
 * An optimistic change with rollback, for toggles and task completion: the
 * cached value changes at once, goes back if the call fails, and the key is
 * refetched either way. Spread the result into `useMutation`.
 */
export function optimistic<TData, TVars>(
  client: Client,
  queryKey: QueryKey,
  apply: (current: TData, vars: TVars) => TData,
) {
  return {
    onMutate: async (vars: TVars): Promise<{ previous: TData | undefined }> => {
      await client.cancelQueries({ queryKey, exact: true });
      const previous = client.getQueryData<TData>(queryKey);
      if (previous !== undefined) client.setQueryData<TData>(queryKey, apply(previous, vars));
      return { previous };
    },
    onError: (
      _error: unknown,
      _vars: TVars,
      context: { previous: TData | undefined } | undefined,
    ) => {
      if (context?.previous !== undefined) client.setQueryData(queryKey, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey, exact: true }),
  };
}
